import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/src/state/store'
import routes from './routes'

Vue.use(VueRouter)

const router = new VueRouter({
    routes,
    // Use the HTML5 history API (i.e. normal-looking routes)
    // instead of routes with hashes (e.g. example.com/#/about).
    // This may require some server configuration in production:
    // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
    mode: 'hash',
    // Simulate native-like scroll behavior when navigating to a new
    // route and using back/forward buttons.
    scrollBehavior(to, from, savedPosition) {
        return savedPosition || {x: 0, y: 0}
    }
})

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
    // Check if auth is required on this route
    // (including nested routes).
    const authRequired = routeTo.matched.some((route) => route.meta.authRequired)
    // If auth isn't required for the route, just continue.
    if (!authRequired) return next()

    // If auth is required and the user is logged in...
    if (store.getters['auth/loggedIn']) {
        // Validate the local user token...
        return store.dispatch('auth/validate').then(validUser => {
            // Then continue if the token still represents a valid user,
            // otherwise redirect to login.
            if (!validUser) redirectToLogin()
            else {
                if (!routeTo.meta.groups || routeTo.meta.groups.includes(validUser.organizationGroup)) next()
                else {
                    next({name: 'home'})
                }
            }
        })
    }

    // If auth is required and the user is NOT currently logged in,
    // redirect to login.
    redirectToLogin()

    function redirectToLogin() {
        // Pass the original route to the login component
        next({name: 'login', query: {redirectFrom: routeTo.fullPath}})
    }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
    // Create a `beforeResolve` hook, which fires whenever
    // `beforeRouteEnter` and `beforeRouteUpdate` would. This
    // allows us to ensure data is fetched even when params change,
    // but the resolved route does not. We put it in `meta` to
    // indicate that it's a hook we created, rather than part of
    // Vue Router (yet?).
    try {
        // For each matched route...
        for (const route of routeTo.matched) {
            await new Promise((resolve, reject) => {
                // If a `beforeResolve` hook is defined, call it with
                // the same arguments as the `beforeEnter` hook.
                if (route.meta && route.meta.beforeResolve) {
                    route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
                        // If the user choose to redirect...
                        if (args.length) {
                            // Complete the redirect.
                            next(...args)
                            reject(new Error('Redirected'))
                        } else {
                            resolve()
                        }
                    })
                } else {
                    // Otherwise, continue resolving the route.
                    resolve()
                }
            })
        }
    }
    // If a `beforeResolve` hook choose to redirect, just return.
    catch (error) {
        return
    }

    // If we reach this point, continue resolving the route.
    next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {/* */})

export default router
